import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Checkbox } from '../components/ui/checkbox'
import { Card, CardContent, CardHeader } from '../components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { useToast } from '../hooks/use-toast'
import api from '../services/api'
import { Memory, MemoryCreate, Category, Organization, Department } from '../types'
import { exportMemoriesAsJSON, exportMemoriesAsCSV } from '../utils/export'
import { organizationService, departmentService } from '../services/organizationService'
import RichTextEditor from '../components/RichTextEditor'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'

export default function MemoriesPage() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [memories, setMemories] = useState<Memory[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [loading, setLoading] = useState(true)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null)
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterSource, setFilterSource] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchMode, setSearchMode] = useState<'text' | 'semantic'>('text')
  const [selectedMemoryIds, setSelectedMemoryIds] = useState<Set<string>>(new Set())
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false)

  // Form state
  const [formData, setFormData] = useState<MemoryCreate>({
    content: '',
    category: 'general',
    source: 'manual',
    importance_score: 0.5,
    tags: [],
    auto_classify: false,
    auto_score: false,
  })

  useEffect(() => {
    loadCategories()
    loadOrganizations()
    loadMemories()
  }, [])

  useEffect(() => {
    loadMemories()
  }, [filterCategory, filterSource, searchQuery, searchMode])

  const loadCategories = async () => {
    try {
      const data = await api.getCategories()
      setCategories(data)
    } catch (error) {
      console.error('Failed to load categories:', error)
      // Use default categories if API fails
      setCategories([])
    }
  }

  const loadOrganizations = async () => {
    try {
      const data = await organizationService.getAll()
      setOrganizations(data)
    } catch (error) {
      console.error('Failed to load organizations:', error)
    }
  }

  const loadDepartments = async (organizationId: string) => {
    try {
      const data = await departmentService.getAll(organizationId)
      setDepartments(data)
    } catch (error) {
      console.error('Failed to load departments:', error)
    }
  }

  const loadMemories = async () => {
    try {
      setLoading(true)
      let data: Memory[]

      // Use semantic search if query exists and mode is semantic
      if (searchQuery && searchMode === 'semantic') {
        const params: any = { query: searchQuery, limit: 50 }
        if (filterCategory !== 'all') params.category = filterCategory
        if (filterSource !== 'all') params.source = filterSource
        data = await api.semanticSearch(params)
      } else {
        // Use regular text search
        const params: any = { limit: 100 }
        if (filterCategory !== 'all') params.category = filterCategory
        if (filterSource !== 'all') params.source = filterSource
        if (searchQuery) params.search = searchQuery
        data = await api.getMemories(params)
      }

      setMemories(data)
    } catch (error) {
      console.error('Failed to load memories:', error)
      toast({
        title: 'Error',
        description: 'Failed to load memories',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCreateMemory = async () => {
    try {
      await api.createMemory(formData)
      toast({
        title: 'Success!',
        description: 'Memory created successfully',
      })
      setIsCreateDialogOpen(false)
      resetForm()
      loadMemories()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create memory',
        variant: 'destructive',
      })
    }
  }

  const handleUpdateMemory = async () => {
    if (!selectedMemory) return

    try {
      await api.updateMemory(selectedMemory.id, formData)
      toast({
        title: 'Success!',
        description: 'Memory updated successfully',
      })
      setIsEditDialogOpen(false)
      setSelectedMemory(null)
      resetForm()
      loadMemories()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to update memory',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteMemory = async (id: string) => {
    if (!confirm('Are you sure you want to delete this memory?')) return

    try {
      await api.deleteMemory(id)
      toast({
        title: 'Success!',
        description: 'Memory deleted successfully',
      })
      loadMemories()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete memory',
        variant: 'destructive',
      })
    }
  }

  const toggleMemorySelection = (memoryId: string) => {
    const newSelection = new Set(selectedMemoryIds)
    if (newSelection.has(memoryId)) {
      newSelection.delete(memoryId)
    } else {
      newSelection.add(memoryId)
    }
    setSelectedMemoryIds(newSelection)
  }

  const toggleSelectAll = () => {
    if (selectedMemoryIds.size === memories.length) {
      setSelectedMemoryIds(new Set())
    } else {
      setSelectedMemoryIds(new Set(memories.map(m => m.id)))
    }
  }

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        Array.from(selectedMemoryIds).map(id => api.deleteMemory(id))
      )
      toast({
        title: 'Success!',
        description: `Deleted ${selectedMemoryIds.size} memories`,
      })
      setSelectedMemoryIds(new Set())
      setShowBulkDeleteDialog(false)
      loadMemories()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete memories',
        variant: 'destructive',
      })
    }
  }

  const handleBulkExport = () => {
    const selectedMemories = memories.filter(m => selectedMemoryIds.has(m.id))
    exportMemoriesAsJSON(selectedMemories, `selected-memories-${Date.now()}.json`)
    toast({
      title: 'Success!',
      description: `Exported ${selectedMemories.length} memories`,
    })
  }

  const openEditDialog = (memory: Memory) => {
    setSelectedMemory(memory)
    setFormData({
      content: memory.content,
      category: memory.category,
      source: memory.source,
      importance_score: memory.importance_score,
      tags: memory.tags || [],
      meta_data: memory.meta_data || undefined,
    })
    setIsEditDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      content: '',
      category: 'general',
      source: 'manual',
      importance_score: 0.5,
      tags: [],
    })
  }

  const sources = ['manual', 'crm', 'social', 'email', 'api']

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Memories</h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Manage your business memories
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate('/categories')}>
              Categories
            </Button>
            <Button variant="outline" onClick={() => navigate('/profile')}>
              Profile
            </Button>
            <Button variant="outline" onClick={() => logout()}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex gap-2 w-full sm:w-auto">
            <Input
              type="text"
              placeholder={searchMode === 'semantic' ? 'Ask a question...' : 'Search memories...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full sm:w-[300px] bg-white"
            />
            <Button
              variant={searchMode === 'semantic' ? 'default' : 'outline'}
              onClick={() => setSearchMode(searchMode === 'text' ? 'semantic' : 'text')}
              className="whitespace-nowrap"
              title={searchMode === 'semantic' ? 'Using AI Semantic Search' : 'Using Text Search'}
            >
              {searchMode === 'semantic' ? '🤖 AI' : '📝 Text'}
            </Button>
          </div>
          
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.name}>
                  {cat.icon && `${cat.icon} `}{cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {sources.map((src) => (
                <SelectItem key={src} value={src}>
                  {src.charAt(0).toUpperCase() + src.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1"></div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Export</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => exportMemoriesAsJSON(memories)}>
                Export All as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportMemoriesAsCSV(memories)}>
                Export All as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {selectedMemoryIds.size > 0 && (
            <>
              <Button variant="outline" onClick={handleBulkExport}>
                Export Selected ({selectedMemoryIds.size})
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowBulkDeleteDialog(true)}
              >
                Delete Selected ({selectedMemoryIds.size})
              </Button>
            </>
          )}

          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>Create Memory</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Memory</DialogTitle>
                <DialogDescription>Add a new memory to your collection</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="content">Content</Label>
                  <RichTextEditor
                    content={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    placeholder="Enter memory content with rich formatting..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.icon && `${cat.icon} `}{cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source">Source</Label>
                    <Select
                      value={formData.source}
                      onValueChange={(value) => setFormData({ ...formData, source: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sources.map((src) => (
                          <SelectItem key={src} value={src}>
                            {src.charAt(0).toUpperCase() + src.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="importance">Importance Score: {formData.importance_score}</Label>
                  <Input
                    id="importance"
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData.importance_score}
                    onChange={(e) =>
                      setFormData({ ...formData, importance_score: parseFloat(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    placeholder="tag1, tag2, tag3"
                    value={formData.tags?.join(', ') || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                      })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="organization">Organization (Optional)</Label>
                    <Select
                      value={formData.organization_id || 'none'}
                      onValueChange={(value) => {
                        const orgId = value === 'none' ? undefined : value
                        setFormData({ ...formData, organization_id: orgId, department_id: undefined })
                        if (orgId) loadDepartments(orgId)
                        else setDepartments([])
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select organization" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="department">Department (Optional)</Label>
                    <Select
                      value={formData.department_id || 'none'}
                      onValueChange={(value) =>
                        setFormData({ ...formData, department_id: value === 'none' ? undefined : value })
                      }
                      disabled={!formData.organization_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* AI Agent Options */}
                <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold text-blue-900">🤖 AI Assistance</Label>
                    <Badge variant="secondary" className="text-xs">Beta</Badge>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="auto-classify"
                      checked={formData.auto_classify}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, auto_classify: checked as boolean })
                      }
                    />
                    <Label htmlFor="auto-classify" className="text-sm cursor-pointer">
                      Auto-classify using AI (suggests best category)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="auto-score"
                      checked={formData.auto_score}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, auto_score: checked as boolean })
                      }
                    />
                    <Label htmlFor="auto-score" className="text-sm cursor-pointer">
                      Auto-score importance using AI (calculates importance)
                    </Label>
                  </div>
                  <p className="text-xs text-blue-700">
                    AI agents analyze your content to suggest optimal category and importance score
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateMemory}>
                  {formData.auto_classify || formData.auto_score ? '✨ Create with AI' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Memories List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : memories.length > 0 ? (
          <>
            {/* Select All */}
            <div className="flex items-center gap-2 mb-4 p-4 bg-white rounded-lg border">
              <Checkbox
                id="select-all"
                checked={selectedMemoryIds.size === memories.length && memories.length > 0}
                onCheckedChange={toggleSelectAll}
              />
              <Label htmlFor="select-all" className="cursor-pointer">
                Select All ({memories.length} memories)
              </Label>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {memories.map((memory) => (
                <Card key={memory.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex gap-3 flex-wrap items-start">
                        <Checkbox
                          checked={selectedMemoryIds.has(memory.id)}
                          onCheckedChange={() => toggleMemorySelection(memory.id)}
                          className="mt-1"
                        />
                        <div className="flex gap-2 flex-wrap">
                          <Badge variant="default">{memory.category}</Badge>
                          <Badge variant="secondary">{memory.source}</Badge>
                          {memory.tags?.map((tag) => (
                            <Badge key={tag} variant="outline">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(memory)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMemory(memory.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div 
                    className="text-sm mb-4 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: memory.content }}
                  />
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>Importance: {(memory.importance_score * 100).toFixed(0)}%</span>
                    <span>{new Date(memory.created_at).toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          </>
        ) : (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground mb-4">No memories found</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>Create Your First Memory</Button>
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Memory</DialogTitle>
              <DialogDescription>Update memory details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-content">Content</Label>
                <RichTextEditor
                  content={formData.content}
                  onChange={(content) => setFormData({ ...formData, content })}
                  placeholder="Edit memory content..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>
                          {cat.icon && `${cat.icon} `}{cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select
                    value={formData.source}
                    onValueChange={(value) => setFormData({ ...formData, source: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sources.map((src) => (
                        <SelectItem key={src} value={src}>
                          {src.charAt(0).toUpperCase() + src.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Importance Score: {formData.importance_score}</Label>
                <Input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.importance_score}
                  onChange={(e) =>
                    setFormData({ ...formData, importance_score: parseFloat(e.target.value) })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={formData.tags?.join(', ') || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                    })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateMemory}>Update</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Selected Memories?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {selectedMemoryIds.size} selected memories? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete {selectedMemoryIds.size} Memories
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  )
}
